/// <reference types="node" />
import { useState, useMemo, useCallback, useEffect } from "react";
import { ResetFiltersButton } from "../../common/ResetFiltersButton.tsx";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Badge from "@mui/material/Badge";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  CalendarToday as CalendarTodayIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { CARD_TITLE_STYLE } from "../../constants/cardStyles";
import {
  AI_DETECTION_DEFAULT_ROWS_PER_PAGE,
  AI_DETECTION_ROWS_PER_PAGE_OPTIONS,
} from "../../constants/aiDetectionPagination";
import { GLOBAL_TOOLTIP_PROPS } from "../../config/tooltips.config.ts";
import { ViewToggle } from "./ViewToggle.tsx";
import { ViewFaceDialog } from "./ViewFaceDialog.tsx";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog.tsx";
import { AddFaceDialog } from "./AddFaceDialog.tsx";
import { formatDateDMY } from "../../utils/dateFormat.ts";
import toast from "react-hot-toast";
import React from "react";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { userTimezoneHeaders } from "../../utils/userTimezoneHeader";
import { readSrIdFromUnknown } from "../../utils/srIdDisplay.ts";

/** Toolbar filter: chevron + clear without overlapping label (Recycle Bin pattern). */
const FACE_TABLE_FILTER_SELECT_PR_CLEAR = "76px";
const FACE_TABLE_FILTER_CLEAR_BTN_RIGHT_PX = 36;

const USE_CASE_CONFIG_API_BASE =
  process.env.REACT_APP_API_URL_FOR_AI_DETECTION || "";

/**
 * API path pattern for face photo URLs returned by the backend.
 * Example: /use-case-configuration/faces/{id}/photos/front
 */
export const FACE_PHOTOS_PATH_PREFIX = "/use-case-configuration/faces";
export const FACE_PHOTO_FIELDS = [
  "front",
  "left_profile",
  "right_profile",
  "up_face",
  "down_face",
] as const;

/** Wrapped API response: { message, status, data } */
interface FacesApiResponse {
  message?: string;
  status?: string;
  data?: FaceEnrollmentApiItem[];
}

/** API response item from GET /use-case-configuration/faces */
interface FaceEnrollmentApiItem {
  id: string;
  name?: string;
  title?: string;
  department?: string;
  email?: string;
  phone?: string | null;
  expiration_date?: string;
  company?: string | null;
  description?: string | null;
  front?: string | null;
  left_profile?: string | null;
  right_profile?: string | null;
  up_face?: string | null;
  down_face?: string | null;
  /**
   * Photo URL paths from API, e.g.:
   * front_url: "/use-case-configuration/faces/{id}/photos/front"
   * left_profile_url: ".../photos/left_profile", right_profile_url, up_face_url, down_face_url
   */
  front_url?: string | null;
  left_profile_url?: string | null;
  right_profile_url?: string | null;
  up_face_url?: string | null;
  down_face_url?: string | null;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string | null;
  /** Serial row id from API — shown in ID column when present */
  sr_id?: string | number;
}

/** Returns true if value looks like a full URL (http/https or data:) */
function isAbsoluteImageUrl(value: string | null | undefined): boolean {
  if (!value || typeof value !== "string") return false;
  const v = value.trim();
  return (
    v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:")
  );
}

/**
 * Build full image URL for a face enrollment when API returns a filename/path.
 * Uses same path pattern as API *_url fields: /use-case-configuration/faces/{id}/photos/{fieldName}.
 */
function getFaceImageUrl(
  baseUrl: string,
  faceId: string,
  fieldValue: string | null | undefined,
  fieldName: (typeof FACE_PHOTO_FIELDS)[number],
): string {
  if (!fieldValue || typeof fieldValue !== "string") return "";
  const trimmed = fieldValue.trim();
  if (isAbsoluteImageUrl(trimmed)) return trimmed;
  const base = baseUrl.replace(/\/$/, "");
  return `${base}${FACE_PHOTOS_PATH_PREFIX}/${faceId}/photos/${fieldName}`;
}

/** Append cache-buster to URL so browser fetches updated image after edit (avoids stale cache). */
export function withCacheBuster(
  url: string | null | undefined,
  version: string | null | undefined,
): string | undefined {
  if (!url || !version) return url ?? undefined;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}t=${encodeURIComponent(version)}`;
}

/** Resolve image URL: use as-is if absolute, else prepend baseUrl (for API *_url relative paths). */
function resolveImageUrl(
  baseUrl: string,
  value: string | null | undefined,
): string {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (isAbsoluteImageUrl(trimmed)) return trimmed;
  const base = baseUrl.replace(/\/$/, "");
  const path = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return `${base}${path}`;
}

/** Build photo URL by convention when API does not provide one (e.g. backend serves at /faces/:id/photos/:field). */
function getDefaultPhotoUrl(
  baseUrl: string,
  faceId: string,
  fieldName: (typeof FACE_PHOTO_FIELDS)[number],
): string {
  const base = baseUrl.replace(/\/$/, "");
  return `${base}${FACE_PHOTOS_PATH_PREFIX}/${faceId}/photos/${fieldName}`;
}

/**
 * Maps API face item to FaceRecognition. Prefers *_url paths from API
 * (front_url, left_profile_url, right_profile_url, up_face_url, down_face_url);
 * fallback builds URL from FACE_PHOTOS_PATH_PREFIX + faceId + photos/{fieldName};
 * final fallback uses URL by convention so images can load even when API omits *_url.
 */
function mapApiItemToFaceRecognition(
  item: FaceEnrollmentApiItem,
  baseUrl: string,
): FaceRecognition {
  const id = String(item.id);
  const frontUrl =
    resolveImageUrl(baseUrl, item.front_url) ||
    getFaceImageUrl(baseUrl, id, item.front, "front") ||
    getDefaultPhotoUrl(baseUrl, id, "front");
  const leftUrl =
    resolveImageUrl(baseUrl, item.left_profile_url) ||
    getFaceImageUrl(baseUrl, id, item.left_profile, "left_profile") ||
    getDefaultPhotoUrl(baseUrl, id, "left_profile");
  const rightUrl =
    resolveImageUrl(baseUrl, item.right_profile_url) ||
    getFaceImageUrl(baseUrl, id, item.right_profile, "right_profile") ||
    getDefaultPhotoUrl(baseUrl, id, "right_profile");
  const upUrl =
    resolveImageUrl(baseUrl, item.up_face_url) ||
    getFaceImageUrl(baseUrl, id, item.up_face, "up_face") ||
    getDefaultPhotoUrl(baseUrl, id, "up_face");
  const downUrl =
    resolveImageUrl(baseUrl, item.down_face_url) ||
    getFaceImageUrl(baseUrl, id, item.down_face, "down_face") ||
    getDefaultPhotoUrl(baseUrl, id, "down_face");
  const mainImage = frontUrl || leftUrl || "";
  const srId = readSrIdFromUnknown(item);
  return {
    id,
    srId,
    name: item.name ?? "",
    image: mainImage,
    title: item.title ?? "",
    department: item.department ?? "",
    company: item.company ?? "",
    expirationDate: item.expiration_date
      ? new Date(item.expiration_date)
      : new Date(),
    isFavorite: false,
    email: item.email,
    phone: item.phone ?? undefined,
    description: item.description ?? undefined,
    images:
      frontUrl || leftUrl || rightUrl || upUrl || downUrl
        ? {
            front: frontUrl || undefined,
            leftProfile: leftUrl || undefined,
            rightProfile: rightUrl || undefined,
            upFace: upUrl || undefined,
            downFace: downUrl || undefined,
          }
        : undefined,
    imageVersion: item.updated_at ?? undefined,
  };
}

/** Optional URLs for the 5 face views (Front, Left, Right, Up, Down) */
export interface FaceRecognitionImages {
  front?: string;
  leftProfile?: string;
  rightProfile?: string;
  upFace?: string;
  downFace?: string;
}

/** Retention period for recycle bin before auto permanent deletion (ms) */
export const RECYCLE_BIN_RETENTION_MS = 60 * 24 * 60 * 60 * 1000; // 60 days

export interface FaceRecognition {
  id: string;
  /** Backend `sr_id` — preferred label in ID column */
  srId?: string;
  name: string;
  image: string;
  title: string;
  department: string;
  company: string;
  expirationDate: Date;
  isFavorite?: boolean;
  email?: string;
  phone?: string;
  description?: string;
  /** All 5 face view images when available */
  images?: FaceRecognitionImages;
  /** From API updated_at; used as cache-buster so updated images load after edit */
  imageVersion?: string;
  /** Deletion timestamp (ms); set when moved to recycle bin; cleared on restore */
  deletedAt?: number;
}

// Sample data
const INITIAL_FACE_DATA: FaceRecognition[] = [
  
];

// Placeholder images for grid view
const PLACEHOLDER_IMAGES = [
  // "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=225&fit=crop",
  // "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=225&fit=crop",
  // "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=225&fit=crop",
  // "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=225&fit=crop",
  // "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=225&fit=crop",
];

type ViewMode = "list" | "grid";
type Order = "asc" | "desc";
type OrderBy = keyof FaceRecognition;

function faceSortByForApi(orderBy: OrderBy): string {
  const map: Partial<Record<OrderBy, string>> = {
    name: "name",
    title: "title",
    department: "department",
    company: "company",
    expirationDate: "expiration_date",
  };
  return map[orderBy] ?? "name";
}

function purgeExpiredRecycleBin<T extends { deletedAt?: number }>(
  items: T[],
): T[] {
  const now = Date.now();
  return items.filter(
    (item) =>
      !item.deletedAt || now - item.deletedAt <= RECYCLE_BIN_RETENTION_MS,
  );
}

export function FaceRecognitionTable() {
  const [faceData, setFaceData] = useState<FaceRecognition[]>(() => {
    try {
      const savedData = localStorage.getItem("face-recognition-data");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        const mapped = parsed.map((item: any) => ({
          ...item,
          expirationDate: new Date(item.expirationDate),
          isFavorite: Boolean(item.isFavorite),
          deletedAt:
            item.deletedAt != null ? Number(item.deletedAt) : undefined,
        }));
        return purgeExpiredRecycleBin(mapped);
      }
    } catch (error) {
      console.error(
        "Error loading face recognition data from localStorage:",
        error,
      );
    }
    return INITIAL_FACE_DATA;
  });
  const [isLoadingFaces, setIsLoadingFaces] = useState(true);
  const [fetchFacesError, setFetchFacesError] = useState<string | null>(null);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<OrderBy>("name");

  const fetchFaces = useCallback(async () => {
    const base = USE_CASE_CONFIG_API_BASE.trim();
    if (!base) {
      setFetchFacesError(
        "API base URL is not configured. Add REACT_APP_API_URL_FOR_AI_DETECTION to your .env file.",
      );
      setIsLoadingFaces(false);
      return;
    }
    setIsLoadingFaces(true);
    setFetchFacesError(null);
    try {
      const query = new URLSearchParams({
        sort_by: faceSortByForApi(orderBy),
        sort_order: order,
      });
      const url = `${base.replace(/\/$/, "")}/use-case-configuration/faces?${query}`;
      const res = await fetch(url, {
        headers: { Accept: "application/json", ...userTimezoneHeaders() },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setFetchFacesError(
          data.message || res.statusText || `Request failed (${res.status}).`,
        );
        setIsLoadingFaces(false);
        return;
      }
      const body: FacesApiResponse | FaceEnrollmentApiItem[] = await res.json();
      const list: FaceEnrollmentApiItem[] = Array.isArray(body)
        ? body
        : body.data != null && Array.isArray(body.data)
          ? body.data
          : [];
      const mapped = list
        .filter((item) => !item.deleted_at)
        .map((item) => mapApiItemToFaceRecognition(item, base));
      setFaceData(mapped);
      try {
        localStorage.setItem("face-recognition-data", JSON.stringify(mapped));
      } catch (e) {
        console.error(e);
      }
    } catch (err) {
      setFetchFacesError(
        err instanceof Error ? err.message : "Failed to load face enrollments.",
      );
    } finally {
      setIsLoadingFaces(false);
    }
  }, [order, orderBy]);

  useEffect(() => {
    fetchFaces();
  }, [fetchFaces]);

  useEffect(() => {
    try {
      localStorage.setItem("face-recognition-data", JSON.stringify(faceData));
    } catch (error) {
      console.error(
        "Error saving face recognition data to localStorage:",
        error,
      );
    }
  }, [faceData]);

  useEffect(() => {
    const loadFromStorage = () => {
      try {
        const savedData = localStorage.getItem("face-recognition-data");
        if (savedData) {
          const parsed = JSON.parse(savedData);
          const mapped = parsed.map((item: any) => ({
            ...item,
            expirationDate: new Date(item.expirationDate),
            isFavorite: Boolean(item.isFavorite),
            deletedAt:
              item.deletedAt != null ? Number(item.deletedAt) : undefined,
          }));
          setFaceData(purgeExpiredRecycleBin(mapped));
        }
      } catch (e) {
        console.error(e);
      }
    };
    window.addEventListener("configure-use-case-fav-changed", loadFromStorage);
    window.addEventListener(
      "configure-use-case-recycle-changed",
      loadFromStorage,
    );
    return () => {
      window.removeEventListener(
        "configure-use-case-fav-changed",
        loadFromStorage,
      );
      window.removeEventListener(
        "configure-use-case-recycle-changed",
        loadFromStorage,
      );
    };
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    AI_DETECTION_DEFAULT_ROWS_PER_PAGE,
  );
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<{
    element: HTMLElement;
    faceItem: FaceRecognition;
    mouseX?: number;
    mouseY?: number;
  } | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogData, setEditDialogData] = useState<FaceRecognition | null>(
    null,
  );
  const [viewDialogData, setViewDialogData] = useState<FaceRecognition | null>(
    null,
  );
  const [viewDialogDisplayId, setViewDialogDisplayId] = useState<string | null>(
    null,
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<FaceRecognition | null>(
    null,
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [filterDateRange, setFilterDateRange] = useState<string>("all");
  const [filterDepartment, setFilterDepartment] = useState<string>("all");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(() => {
    try {
      return localStorage.getItem("configure-face-show-favorites") === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(
        "configure-face-show-favorites",
        String(showFavoritesOnly),
      );
    } catch (e) {
      console.error(e);
    }
  }, [showFavoritesOnly]);

  const handleViewModeChange = useCallback((newMode: ViewMode) => {
    setViewMode(newMode);
  }, []);

  const handleRequestSort = useCallback(
    (property: OrderBy) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
      setPage(0);
    },
    [order, orderBy],
  );

  /** Client-side filtering: search and filters apply to full fetched dataset; no API call on search. */
  const filteredAndSorted = useMemo(() => {
    let filtered = faceData.filter((item) => !item.deletedAt);

    if (searchQuery) {
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (item.srId ?? "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.company.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Date filter (values: "all" | "expiring-soon" | "next-3" | "next-6")
    if (filterDateRange !== "all") {
      const now = new Date();
      let start: number;
      let end: number;
      if (filterDateRange === "expiring-soon") {
        start = now.getTime();
        end = new Date(now.getFullYear(), now.getMonth() + 1, now.getDate()).getTime();
      } else if (filterDateRange === "next-3") {
        start = now.getTime();
        end = new Date(now.getFullYear(), now.getMonth() + 3, now.getDate()).getTime();
      } else if (filterDateRange === "next-6") {
        start = now.getTime();
        end = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate()).getTime();
      } else {
        start = 0;
        end = Number.MAX_SAFE_INTEGER;
      }
      filtered = filtered.filter((item) => {
        const date = item.expirationDate.getTime();
        return date >= start && date <= end;
      });
    }

    // Department filter
    if (filterDepartment !== "all") {
      filtered = filtered.filter(
        (item) => item.department === filterDepartment,
      );
    }

    if (showFavoritesOnly) {
      filtered = filtered.filter((item) => item.isFavorite);
    }

    return filtered;
  }, [
    faceData,
    searchQuery,
    filterDateRange,
    filterDepartment,
    showFavoritesOnly,
  ]);

  const favoritesCount = useMemo(
    () => faceData.filter((item) => item.isFavorite).length,
    [faceData],
  );

  /** Pagination over filtered results (client-side); recalculated when filters/search change. */
  const paginatedData = useMemo(() => {
    return filteredAndSorted.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );
  }, [filteredAndSorted, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, showFavoritesOnly, filterDateRange, filterDepartment]);

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    showFavoritesOnly ||
    filterDateRange !== "all" ||
    filterDepartment !== "all";

  const handleResetFilters = () => {
    setSearchQuery("");
    setShowFavoritesOnly(false);
    setFilterDateRange("all");
    setFilterDepartment("all");
    setPage(0);
  };

  const handleMenuClick = useCallback(
    (event: React.MouseEvent<HTMLElement>, faceItem: FaceRecognition) => {
      event.stopPropagation();
      setAnchorEl({ element: event.currentTarget, faceItem });
    },
    [],
  );

  const handleContextMenu = useCallback(
    (event: React.MouseEvent, faceItem: FaceRecognition) => {
      event.preventDefault();
      setAnchorEl({
        element: event.currentTarget as HTMLElement,
        faceItem,
        mouseX: event.clientX,
        mouseY: event.clientY,
      });
    },
    [],
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleToggleFavorite = useCallback((item: FaceRecognition) => {
    setFaceData((prev) =>
      prev.map((f) =>
        f.id === item.id ? { ...f, isFavorite: !f.isFavorite } : f,
      ),
    );
  }, []);

  const openFaceViewDialog = useCallback(
    (face: FaceRecognition, listIndex: number) => {
      const displayId =
        face.srId?.trim() ||
        (/^FR-\d{3}$/.test(face.id ?? "")
          ? (face.id ?? "")
          : `FR-${String(page * rowsPerPage + listIndex + 1).padStart(3, "0")}`);
      setViewDialogDisplayId(displayId);
      setViewDialogData(face);
    },
    [page, rowsPerPage],
  );

  const handleView = useCallback(() => {
    if (anchorEl) {
      const idx = paginatedData.findIndex((f) => f.id === anchorEl.faceItem.id);
      openFaceViewDialog(anchorEl.faceItem, idx >= 0 ? idx : 0);
    }
    handleMenuClose();
  }, [anchorEl, handleMenuClose, paginatedData, openFaceViewDialog]);

  const handleEdit = useCallback(() => {
    if (anchorEl) {
      setEditDialogData(anchorEl.faceItem);
    }
    handleMenuClose();
  }, [anchorEl, handleMenuClose]);

  const handleDeleteClick = useCallback(() => {
    if (anchorEl) {
      setItemToDelete(anchorEl.faceItem);
      setDeleteError(null);
      setDeleteDialogOpen(true);
    }
    handleMenuClose();
  }, [anchorEl, handleMenuClose]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!itemToDelete) return;
    const base = USE_CASE_CONFIG_API_BASE.replace(/\/$/, "");
    if (!base) {
      setDeleteError("API base URL is not configured.");
      return;
    }
    setDeleteLoading(true);
    setDeleteError(null);
    const enrollmentId = encodeURIComponent(itemToDelete.id);
    const url = `${base}/use-case-configuration/faces/${enrollmentId}`;
    try {
      const res = await fetch(url, {
        method: "DELETE",
        headers: { Accept: "application/json", ...userTimezoneHeaders() },
      });
      if (res.status === 204) {
        window.dispatchEvent(
          new CustomEvent("configure-use-case-recycle-changed"),
        );
        setItemToDelete(null);
        setDeleteDialogOpen(false);
        await fetchFaces();
        toast.success("Face enrollment moved to recycle bin", {
          position: "bottom-center",
        });
      } else if (res.status === 422) {
        const data = await res.json().catch(() => ({}));
        const msg = data?.detail?.[0]?.msg ?? "Validation failed";
        setDeleteError(msg);
      } else {
        setDeleteError(res.statusText || "Failed to delete face enrollment.");
      }
    } catch (err) {
      setDeleteError(
        err instanceof Error
          ? err.message
          : "Failed to delete face enrollment.",
      );
    } finally {
      setDeleteLoading(false);
    }
  }, [itemToDelete, fetchFaces]);

  const formatDate = (date: Date) => {
    return formatDateDMY(date);
  };

  const handleAddFace = (faceData: any) => {
    const newFace: FaceRecognition = {
      id: `FR-${String(Math.floor(Math.random() * 10000)).padStart(3, "0")}`,
      ...faceData,
    };
    setFaceData((prev) => [...prev, newFace]);
    fetchFaces();
    toast.success("Face enrollment created successfully", {
      position: "bottom-center",
    });
  };

  const handleUpdateFace = (faceData: any) => {
    setFaceData((prev) =>
      prev.map((item) =>
        item.id === faceData.id ? { ...item, ...faceData } : item,
      ),
    );
    fetchFaces();
    toast.success("Face enrollment updated successfully", {
      position: "bottom-center",
    });
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleNameClick = (face: FaceRecognition) => {
    const idx = paginatedData.findIndex((f) => f.id === face.id);
    openFaceViewDialog(face, idx >= 0 ? idx : 0);
  };

  // Get unique departments for filter
  const departments = useMemo(() => {
    const uniqueDepts = Array.from(
      new Set(faceData.map((item) => item.department)),
    );
    return uniqueDepts.sort();
  }, [faceData]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#fff",
      }}
    >
      {/* Header with Title and Info Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 1, sm: 2 },
          pt: 2,
          pb: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: { xs: "14px", sm: "16px" },
              fontWeight: 400,
              color: "#FF8555",
            }}
          >
            Face Recognition
          </Typography>
          <Tooltip
            title="Manage face recognition entries for site access control"
            {...GLOBAL_TOOLTIP_PROPS}
          >
            <IconButton
              size="small"
              sx={{
                color: "#9E9E9E",
                padding: "2px",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <InfoIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Search, Filters, and Actions Row */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          px: { xs: 1, sm: 2 },
          pb: 2,
          alignItems: "center",
        }}
      >
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            width: { xs: "100%", sm: "240px" },
            minWidth: { xs: 0, sm: 240 },
            flex: { xs: "1 1 100%", sm: "0 0 auto" },
            "& .MuiOutlinedInput-root": {
              height: 36,
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              bgcolor: "#fff",
              borderRadius: "10px",
              "& fieldset": {
                borderColor: "#E5E7EB",
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
              </InputAdornment>
            ),
          }}
        />

        {/* Favorites Filter */}
        <Tooltip
          title={showFavoritesOnly ? "Show all" : "Show favorites only"}
          {...GLOBAL_TOOLTIP_PROPS}
        >
          <IconButton
            size="small"
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            sx={{
              border: "1px solid #E5E7EB",
              borderRadius: "10px",
              height: 36,
              width: 36,
              bgcolor: showFavoritesOnly ? "#FFE0D0" : "#fff",
              "&:hover": {
                bgcolor: showFavoritesOnly ? "#FFD0B8" : "#F5F5F5",
              },
            }}
          >
            <Badge
              badgeContent={favoritesCount}
              color="primary"
              sx={{
                "& .MuiBadge-badge": {
                  fontSize: "10px",
                  height: "16px",
                  minWidth: "16px",
                  bgcolor: "#2932E5",
                },
              }}
            >
              {showFavoritesOnly ? (
                <StarIcon fontSize="small" sx={{ color: "#FF8555" }} />
              ) : (
                <StarBorderIcon fontSize="small" sx={{ color: "#9E9E9E" }} />
              )}
            </Badge>
          </IconButton>
        </Tooltip>

        {/* View Toggle */}
        <ViewToggle
          view={viewMode}
          onChange={handleViewModeChange}
          ariaLabel="Toggle face recognition view mode"
        />

        {/* Date Filter (expiry) */}
        <FormControl
          size="small"
          sx={{ minWidth: { xs: 100, sm: 140 }, position: "relative" }}
        >
          <Select
            value={filterDateRange}
            onChange={(e) => setFilterDateRange(e.target.value)}
            displayEmpty
            renderValue={(v) => {
              const labels: Record<string, string> = {
                all: "All Expiry Dates",
                "expiring-soon": "Expiring Soon",
                "next-3": "Next 3 Months",
                "next-6": "Next 6 Months",
              };
              return labels[v ?? "all"] ?? "All Expiry Dates";
            }}
            sx={{
              height: 36,
              minWidth: { xs: 100, sm: 140 },
              borderRadius: "10px",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              bgcolor: filterDateRange !== "all" ? "#FFE0D0" : "#fff",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                paddingRight:
                  filterDateRange !== "all"
                    ? `${FACE_TABLE_FILTER_SELECT_PR_CLEAR} !important`
                    : undefined,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
              },
              "&:hover": {
                bgcolor: filterDateRange !== "all" ? "#FFE0D0" : "#f5f5f5",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2932E5",
              },
            }}
            startAdornment={
              <CalendarTodayIcon
                sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }}
              />
            }
          >
            <MenuItem value="all">All Expiry Dates</MenuItem>
            <MenuItem value="expiring-soon">Expiring Soon</MenuItem>
            <MenuItem value="next-3">Next 3 Months</MenuItem>
            <MenuItem value="next-6">Next 6 Months</MenuItem>
          </Select>
          {filterDateRange !== "all" ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFilterDateRange("all");
              }}
              aria-label="Clear expiry date filter"
              sx={{
                position: "absolute",
                right: `${FACE_TABLE_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                p: 0.25,
                width: 28,
                height: 28,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </FormControl>

        {/* Department Filter */}
        <FormControl
          size="small"
          sx={{ minWidth: { xs: 120, sm: 150 }, position: "relative" }}
        >
          <Select
            value={filterDepartment}
            onChange={(e) => setFilterDepartment(e.target.value)}
            displayEmpty
            sx={{
              height: 36,
              minWidth: { xs: 120, sm: 150 },
              borderRadius: "10px",
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              bgcolor: filterDepartment !== "all" ? "#FFE0D0" : "#fff",
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                paddingRight:
                  filterDepartment !== "all"
                    ? `${FACE_TABLE_FILTER_SELECT_PR_CLEAR} !important`
                    : undefined,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#E5E7EB",
              },
              "&:hover": {
                bgcolor: filterDepartment !== "all" ? "#FFE0D0" : "#f5f5f5",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2932E5",
              },
            }}
            startAdornment={
              <FilterListIcon sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }} />
            }
          >
            <MenuItem value="all">All Departments</MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept} value={dept}>
                {dept}
              </MenuItem>
            ))}
          </Select>
          {filterDepartment !== "all" ? (
            <IconButton
              size="small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setFilterDepartment("all");
              }}
              aria-label="Clear department filter"
              sx={{
                position: "absolute",
                right: `${FACE_TABLE_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                p: 0.25,
                width: 28,
                height: 28,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </FormControl>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Add Face Button */}
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          sx={{
            bgcolor: "#2932E5",
            border: "0.8px solid #2932E5",
            borderRadius: "10px",
            color: "#fff",
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            fontSize: { xs: "13px", sm: "14px" },
            px: { xs: 1.5, sm: 2 },
            flexShrink: 0,
            fontWeight: 500,
            height: 36,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#1f28b8",
              border: "0.8px solid #1f28b8",
              boxShadow: "none",
            },
          }}
        >
          Add Face
        </Button>

        <ResetFiltersButton
          disabled={!hasActiveFilters}
          onClick={handleResetFilters}
        />
      </Box>

      {/* Error / Loading / Empty / No results / Table or Grid - same order as Manage Use Case tab */}
      {fetchFacesError ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 48, color: "#B71C1C" }} />
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              color: "#4A5565",
              textAlign: "center",
            }}
          >
            {fetchFacesError}
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchFaces}
            sx={{
              bgcolor: "#2932E5",
              textTransform: "none",
              "&:hover": { bgcolor: "#1f28b8" },
            }}
          >
            Retry
          </Button>
        </Box>
      ) : isLoadingFaces ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#6B7280",
            }}
          >
            Loading face enrollments...
          </Typography>
        </Box>
      ) : faceData.filter((item) => !item.deletedAt).length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            width={200}
            height={200}
            focusable="false"
            aria-hidden="true"
            style={{ display: "block", color: "#D1D5DB" }}
          >
            <path
              fill="currentColor"
              d="M9 11.75c-.69 0-1.25.56-1.25 1.25s.56 1.25 1.25 1.25 1.25-.56 1.25-1.25-.56-1.25-1.25-1.25m6 0c-.69 0-1.25.56-1.25 1.25s.56 1.25 1.25 1.25 1.25-.56 1.25-1.25-.56-1.25-1.25-1.25M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8 0-.29.02-.58.05-.86 2.36-1.05 4.23-2.98 5.21-5.37C11.07 8.33 14.05 10 17.42 10c.78 0 1.53-.09 2.25-.26.21.71.33 1.47.33 2.26 0 4.41-3.59 8-8 8"
            />
          </Box>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            No face enrollments available.
          </Typography>
        </Box>
      ) : filteredAndSorted.length === 0 &&
        showFavoritesOnly &&
        favoritesCount === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <StarBorderIcon sx={{ fontSize: 40, color: "#D1D5DB" }} />
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              fontWeight: 500,
              color: "#4B5563",
              textAlign: "center",
            }}
          >
            No favorites yet
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            Click the star icon next to faces to add them to favorites.
          </Typography>
        </Box>
      ) : filteredAndSorted.length === 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            p: 4,
            bgcolor: "#FAFAFA",
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 24 24"
            focusable="false"
            aria-hidden="true"
            width={150}
            height={150}
            style={{ display: "block", color: "#D1D5DB" }}
          >
            <path
              fill="currentColor"
              d="M9 11.75c-.69 0-1.25.56-1.25 1.25s.56 1.25 1.25 1.25 1.25-.56 1.25-1.25-.56-1.25-1.25-1.25m6 0c-.69 0-1.25.56-1.25 1.25s.56 1.25 1.25 1.25 1.25-.56 1.25-1.25-.56-1.25-1.25-1.25M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8 0-.29.02-.58.05-.86 2.36-1.05 4.23-2.98 5.21-5.37C11.07 8.33 14.05 10 17.42 10c.78 0 1.53-.09 2.25-.26.21.71.33 1.47.33 2.26 0 4.41-3.59 8-8 8"
            />
          </Box>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "16px",
              color: "#6B7280",
              textAlign: "center",
            }}
          >
            No face enrollments match your filters.
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: "14px",
              color: "#9CA3AF",
              textAlign: "center",
            }}
          >
            Try adjusting your search or filters.
          </Typography>
        </Box>
      ) : viewMode === "list" ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            width: "100%",
            overflow: "hidden",
          }}
        >
          {/* flex:1 + minHeight:0 reserves space above footer so pagination is never clipped */}
          <TableContainer
            sx={{
              width: "100%",
              flex: 1,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{ tableLayout: "fixed", minWidth: 800 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      bgcolor: "#FAFAFA",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#2932E5",
                      borderBottom: "1px solid #E5E7EB",
                      px: "10px",
                      py: "8px",
                      verticalAlign: "middle",
                      width: 100,
                      minWidth: 100,
                    }}
                  >
                    ID
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: "#FAFAFA",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#2932E5",
                      borderBottom: "1px solid #E5E7EB",
                      px: "10px",
                      py: "8px",
                      verticalAlign: "middle",
                      cursor: "pointer",
                      width: 180,
                      minWidth: 180,
                    }}
                    onClick={() => handleRequestSort("name")}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      Name
                      {orderBy === "name" &&
                        (order === "asc" ? (
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: "#FAFAFA",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#2932E5",
                      borderBottom: "1px solid #E5E7EB",
                      px: "10px",
                      py: "8px",
                      verticalAlign: "middle",
                      zIndex: 11,
                      width: "200px",
                    }}
                  >
                    Image
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: "#FAFAFA",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#2932E5",
                      borderBottom: "1px solid #E5E7EB",
                      px: "10px",
                      py: "8px",
                      verticalAlign: "middle",
                      cursor: "pointer",
                      width: 160,
                      minWidth: 160,
                    }}
                    onClick={() => handleRequestSort("title")}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      Title
                      {orderBy === "title" &&
                        (order === "asc" ? (
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: "#FAFAFA",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#2932E5",
                      borderBottom: "1px solid #E5E7EB",
                      px: "10px",
                      py: "8px",
                      verticalAlign: "middle",
                      cursor: "pointer",
                      width: 150,
                      minWidth: 150,
                    }}
                    onClick={() => handleRequestSort("department")}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      Department
                      {orderBy === "department" &&
                        (order === "asc" ? (
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: "#FAFAFA",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#2932E5",
                      borderBottom: "1px solid #E5E7EB",
                      px: "10px",
                      py: "8px",
                      verticalAlign: "middle",
                      cursor: "pointer",
                      width: 180,
                      minWidth: 180,
                    }}
                    onClick={() => handleRequestSort("company")}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      Company
                      {orderBy === "company" &&
                        (order === "asc" ? (
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      bgcolor: "#FAFAFA",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#2932E5",
                      borderBottom: "1px solid #E5E7EB",
                      px: "10px",
                      py: "8px",
                      verticalAlign: "middle",
                      cursor: "pointer",
                      width: 110,
                      minWidth: 110,
                    }}
                    onClick={() => handleRequestSort("expirationDate")}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      Expiration
                      {orderBy === "expirationDate" &&
                        (order === "asc" ? (
                          <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                          <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        ))}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      bgcolor: "#FAFAFA",
                      fontFamily: "Roboto, sans-serif",
                      fontSize: "14px",
                      fontWeight: 500,
                      color:
                        hoveredRowId != null || anchorEl != null
                          ? "#2932E5"
                          : "transparent",
                      borderBottom: "1px solid #E5E7EB",
                      px: "10px",
                      py: "8px",
                      verticalAlign: "middle",
                      width: 80,
                      transition: "color 0.2s",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item, index) => {
                  const displayId =
                    item.srId?.trim() ||
                    (/^FR-\d{3}$/.test(item.id ?? "")
                      ? (item.id ?? "")
                      : `FR-${String(page * rowsPerPage + index + 1).padStart(3, "0")}`);
                  return (
                    <TableRow
                      key={item.id}
                      onMouseEnter={() => setHoveredRowId(item.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      onContextMenu={(e) => handleContextMenu(e, item)}
                      sx={{
                        "&:hover": {
                          bgcolor: "rgba(0, 0, 0, 0.02)",
                        },
                      }}
                    >
                      <TableCell
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          color: "#4A5565",
                          px: "10px",
                          py: "8px",
                          verticalAlign: "middle",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        {displayId}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          color: "#2932E5",
                          px: "10px",
                          py: "8px",
                          verticalAlign: "middle",
                          borderBottom: "1px solid #E5E7EB",
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                        onClick={() => handleNameClick(item)}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          {item.name ?? ""}
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleFavorite(item);
                            }}
                            sx={{
                              padding: "4px",
                              opacity: hoveredRowId === item.id ? 1 : 0,
                              transition: "opacity 0.2s",
                            }}
                          >
                            {item.isFavorite ? (
                              <StarIcon
                                sx={{ fontSize: 16, color: "#FFB400" }}
                              />
                            ) : (
                              <StarBorderIcon
                                sx={{ fontSize: 16, color: "#9E9E9E" }}
                              />
                            )}
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          px: "10px",
                          py: "8px",
                          verticalAlign: "middle",
                          borderBottom: "1px solid #E5E7EB",
                          overflow: "visible",
                          width: 1,
                          minWidth: 120,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            direction: "ltr",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            flexWrap: "nowrap",
                            overflow: "visible",
                            width: "fit-content",
                            minWidth: 32,
                            ml: "72px",
                          }}
                        >
                          {(() => {
                            const allUrls = [
                              item.images?.downFace,
                              item.images?.upFace,
                              item.images?.rightProfile,
                              item.images?.leftProfile,
                              item.images?.front,
                            ].filter(Boolean) as string[];
                            if (allUrls.length === 0) {
                              return (
                                <Avatar
                                  src={
                                    item.image?.startsWith("blob:")
                                      ? undefined
                                      : (withCacheBuster(
                                          item.image,
                                          item.imageVersion,
                                        ) ?? item.image)
                                  }
                                  alt={item.name}
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    flexShrink: 0,
                                    bgcolor: "#E5E7EB",
                                    color: "#4A5565",
                                    fontSize: "14px",
                                    overflow: "hidden",
                                  }}
                                >
                                  {(!item.image ||
                                    item.image.startsWith("blob:")) &&
                                    (item.name.trim().charAt(0).toUpperCase() ||
                                      "?")}
                                </Avatar>
                              );
                            }
                            const overlap = 6;
                            const avatarSize = 28;
                            const avatarSx = {
                              width: avatarSize,
                              height: avatarSize,
                              minWidth: avatarSize,
                              flexShrink: 0,
                              border: "2px solid #fff",
                              boxSizing: "border-box" as const,
                              bgcolor: "#E5E7EB",
                              color: "#4A5565",
                              fontSize: "11px",
                              overflow: "hidden" as const,
                              // mr: "-48px"
                            };
                            return (
                              <>
                                {allUrls.map((url, i) => (
                                  <Avatar
                                    key={`${item.id}-img-${i}`}
                                    src={
                                      url.startsWith("blob:")
                                        ? url
                                        : (withCacheBuster(
                                            url,
                                            item.imageVersion,
                                          ) ?? url)
                                    }
                                    alt={`${item.name} ${i + 1}`}
                                    slotProps={{ img: { loading: "lazy" } }}
                                    sx={{
                                      ...avatarSx,
                                      ml: i === 0 ? 0 : -overlap,
                                      zIndex: i,
                                    }}
                                  />
                                ))}
                              </>
                            );
                          })()}
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          color: "#4A5565",
                          px: "10px",
                          py: "8px",
                          verticalAlign: "middle",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        {item.title ?? ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          color: "#4A5565",
                          px: "10px",
                          py: "8px",
                          verticalAlign: "middle",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        {item.department ?? ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          color: "#4A5565",
                          px: "10px",
                          py: "8px",
                          verticalAlign: "middle",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        {item.company ?? ""}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Roboto, sans-serif",
                          fontSize: "14px",
                          color: "#4A5565",
                          px: "10px",
                          py: "8px",
                          verticalAlign: "middle",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        {item.expirationDate
                          ? formatDate(item.expirationDate)
                          : ""}
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{
                          px: "10px",
                          py: "8px",
                          verticalAlign: "middle",
                          borderBottom: "1px solid #E5E7EB",
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, item)}
                          sx={{
                            opacity: hoveredRowId === item.id ? 1 : 0,
                            transition: "opacity 0.2s",
                          }}
                        >
                          <MoreVertIcon
                            sx={{ fontSize: 20, color: "#4A5565" }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredAndSorted.length}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[...AI_DETECTION_ROWS_PER_PAGE_OPTIONS]}
            sx={{
              borderTop: "1px solid #E5E7EB",
              bgcolor: "#F2F2F2",
              flexShrink: 0,
              width: "100%",
              boxSizing: "border-box",
              "& .MuiTablePagination-toolbar": {
                minHeight: { xs: 44, sm: 52 },
                px: { xs: 1, sm: 2 },
                flexWrap: "wrap",
                justifyContent: { xs: "center", sm: "flex-end" },
              },
              "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                {
                  fontFamily: "Roboto, sans-serif",
                  fontSize: { xs: "12px", sm: "14px" },
                  color: "#4A5565",
                  m: 0,
                },
              "& .MuiTablePagination-actions": {
                color: "#4A5565",
              },
            }}
          />
        </Box>
      ) : (
        /* Grid: scrollable area + footer same as list */
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            width: "100%",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: "auto",
              p: { xs: 0.5, sm: 0 },
            }}
          >
            <Grid container spacing={1}>
              {paginatedData.map((item, index) => {
                const displayId =
                  item.srId?.trim() ||
                  (/^FR-\d{3}$/.test(item.id ?? "")
                    ? (item.id ?? "")
                    : `FR-${String(page * rowsPerPage + index + 1).padStart(3, "0")}`);
                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={3}
                    xl={2}
                    key={item.id}
                    sx={{ display: "flex", minWidth: 0 }}
                  >
                    <Card
                      onMouseEnter={() => setHoveredRowId(item.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      onClick={() => handleNameClick(item)}
                      onContextMenu={(e) => handleContextMenu(e, item)}
                      sx={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        minWidth: 0,
                        minHeight: { xs: 300, sm: 340 },
                        bgcolor: "rgb(255, 255, 255)",
                        color: "rgb(33, 33, 33)",
                        boxShadow: "var(--Paper-shadow)",
                        backgroundImage: "var(--Paper-overlay)",
                        overflow: "hidden",
                        borderRadius: "14px",
                        transition: "0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        cursor: "pointer",
                        border: "1px solid rgb(229, 231, 235)",
                        "&:hover": {
                          boxShadow: "0 8px 16px rgba(0,0,0,0.12)",
                          transform: "translateY(-4px) scale(1.02)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          flexShrink: 0,
                          minWidth: 0,
                          alignSelf: "stretch",
                          px: { xs: 1.25, sm: 1.5 },
                          pt: { xs: 1.25, sm: 1.5 },
                          pb: { xs: 0.75, sm: 1 },
                        }}
                      >
                        <Box
                          sx={{
                            width: "100%",
                            aspectRatio: "1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "rgb(33, 33, 33)",
                            borderRadius: "8px",
                            overflow: "hidden",
                            position: "relative",
                            backgroundImage:
                              item.image && !item.image.startsWith("blob:")
                                ? `url(${withCacheBuster(item.image, item.imageVersion) ?? item.image})`
                                : `url(${PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuClick(e, item)}
                            aria-label="Open actions menu"
                            sx={{
                              position: "absolute",
                              top: 8,
                              left: 8,
                              zIndex: 1,
                              color: "rgba(255,255,255,0.95)",
                              bgcolor: "rgba(255,255,255,0.15)",
                              padding: "6px",
                              "&:hover": {
                                bgcolor: "rgba(255,255,255,0.3)",
                                color: "#fff",
                              },
                            }}
                          >
                            <MoreVertIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleFavorite(item);
                            }}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              zIndex: 1,
                              color: item.isFavorite
                                ? "#FFB400"
                                : "rgba(255,255,255,0.95)",
                              bgcolor: "rgba(255,255,255,0.15)",
                              padding: "6px",
                              "&:hover": {
                                bgcolor: "rgba(255,255,255,0.3)",
                                color: item.isFavorite ? "#FFB400" : "#fff",
                              },
                            }}
                          >
                            {item.isFavorite ? (
                              <StarIcon sx={{ fontSize: 20 }} />
                            ) : (
                              <StarBorderIcon sx={{ fontSize: 20 }} />
                            )}
                          </IconButton>
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 10,
                              left: 10,
                              zIndex: 1,
                              px: 1.25,
                              py: 0.5,
                              borderRadius: "20px",
                              bgcolor: "rgba(255, 179, 128, 0.92)",
                              color: "#fff",
                              fontFamily: "Roboto, sans-serif",
                              fontSize: "11px",
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                              textTransform: "uppercase",
                            }}
                          >
                            {(item.department || "FACE").toUpperCase()}
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          p: { xs: 1, sm: 1.5 },
                          pt: { xs: 1, sm: 1.25 },
                          flex: 1,
                          minHeight: { xs: 60, sm: 72 },
                          minWidth: 0,
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "flex-start",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: { xs: "14px", sm: "16px" },
                            fontWeight: 700,
                            color: "#212121",
                            mb: 0.5,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: { xs: "12px", sm: "13px" },
                            color: "#4A5565",
                            mb: 0.25,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "Roboto, sans-serif",
                            fontSize: { xs: "12px", sm: "13px" },
                            color: "#4A5565",
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          ID: {displayId}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
          <TablePagination
            component="div"
            count={filteredAndSorted.length}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[...AI_DETECTION_ROWS_PER_PAGE_OPTIONS]}
            sx={{
              borderTop: "1px solid #E5E7EB",
              bgcolor: "#F2F2F2",
              flexShrink: 0,
              width: "100%",
              boxSizing: "border-box",
              "& .MuiTablePagination-toolbar": {
                minHeight: { xs: 44, sm: 52 },
                px: { xs: 1, sm: 2 },
                flexWrap: "wrap",
                justifyContent: { xs: "center", sm: "flex-end" },
              },
              "& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel":
                {
                  fontFamily: "Roboto, sans-serif",
                  fontSize: { xs: "12px", sm: "14px" },
                  color: "#4A5565",
                  m: 0,
                },
              "& .MuiTablePagination-actions": {
                color: "#4A5565",
              },
            }}
          />
        </Box>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl?.element}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorReference={
          anchorEl?.mouseX != null ? "anchorPosition" : "anchorEl"
        }
        anchorPosition={
          anchorEl?.mouseX != null && anchorEl?.mouseY != null
            ? { top: anchorEl.mouseY, left: anchorEl.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleView}>
          <VisibilityIcon sx={{ fontSize: 18, mr: 1 }} />
          View
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ fontSize: 18, mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick}>
          <DeleteIcon sx={{ fontSize: 18, mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Add Face Dialog */}
      <AddFaceDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSave={handleAddFace}
      />

      {/* Edit Face Dialog */}
      <AddFaceDialog
        open={!!editDialogData}
        onClose={() => setEditDialogData(null)}
        onSave={handleUpdateFace}
        editData={editDialogData}
      />

      {/* View Dialog */}
      <ViewFaceDialog
        open={!!viewDialogData}
        onClose={() => {
          setViewDialogData(null);
          setViewDialogDisplayId(null);
        }}
        face={viewDialogData}
        displayId={viewDialogDisplayId}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
          setDeleteError(null);
        }}
        onConfirm={handleDeleteConfirm}
        confirmLoading={deleteLoading}
        error={deleteError}
        title="Delete Face Recognition Entry"
        message={
          itemToDelete
            ? `Are you sure you want to delete "${itemToDelete.name}"? Confirmation is required. The record will be moved to the Recycle Bin, retained for 60 days. After 60 days it will be permanently deleted.`
            : ""
        }
      />
    </Box>
  );
}
